/* ============================================================================
   MR GRIP — "QUIET MACHINE" motion layer
   Original, dependency-free scroll/interaction engine for the Mr Grip theme.

   Everything degrades to a static, fully readable page when JS is off or when
   the visitor prefers reduced motion. Nothing here touches price, cart totals
   or checkout — those stay on Shopify's own native form + payment buttons.
   ========================================================================= */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESKTOP = window.matchMedia('(min-width: 900px)');

  function reduced() { return REDUCED.matches; }

  /* Arm the CSS reveal states as early as possible. Until this class exists
     every .mg-rise / .mg-wipe / .mg-slide renders in its final, visible form,
     so a JS failure degrades to a plain page instead of a blank one. */
  if (!reduced()) document.documentElement.classList.add('mg-anim');
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts || false); }
  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* ---- rAF-throttled scroll bus --------------------------------------- */
  var readers = [];
  var ticking = false;
  function pump() {
    ticking = false;
    var y = window.pageYOffset;
    var vh = window.innerHeight;
    for (var i = 0; i < readers.length; i++) {
      try { readers[i](y, vh); } catch (e) { /* keep the bus alive */ }
    }
  }
  function schedule() {
    /* A hidden tab never services requestAnimationFrame, which would leave
       every scroll-driven value frozen at whatever it was when the tab was
       backgrounded. Run synchronously in that case. */
    if (document.hidden) { pump(); return; }
    if (!ticking) { ticking = true; requestAnimationFrame(pump); }
  }
  function subscribe(fn) { readers.push(fn); schedule(); }

  on(window, 'scroll', schedule, { passive: true });
  on(window, 'resize', schedule, { passive: true });

  /* ---- 1. reveal on enter --------------------------------------------- */
  function initReveal() {
    var items = all('.mg-rise, .mg-wipe, .mg-grow');
    if (!items.length) return;
    if (reduced() || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---- 2. sticky nav --------------------------------------------------- */
  function initNav() {
    var nav = document.querySelector('.mg-nav');
    if (!nav) return;
    var last = 0;
    /* Pinned by default: the header carries the range menu and the cart, and
       a bar that slides away on every downward scroll makes both feel out of
       reach. Hiding is still available from the editor for anyone who wants
       the extra vertical space. */
    var mayHide = nav.dataset.nav === 'hide';
    subscribe(function (y) {
      nav.dataset.solid = y > 24 ? 'true' : 'false';
      if (!mayHide) { nav.dataset.hidden = 'false'; return; }
      var hide = y > 260 && y > last + 4;
      var show = y < last - 4 || y < 120;
      if (hide) nav.dataset.hidden = 'true';
      else if (show) nav.dataset.hidden = 'false';
      last = y;
    });

    var burger = nav.querySelector('.mg-burger');
    var drawer = document.querySelector('.mg-drawer-nav');
    if (burger && drawer) {
      var close = drawer.querySelector('.mg-drawer-nav__close');
      var setOpen = function (open) {
        drawer.dataset.open = open ? 'true' : 'false';
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.style.overflow = open ? 'hidden' : '';
        if (open) { var f = drawer.querySelector('a'); if (f) f.focus(); }
        else burger.focus();
      };
      on(burger, 'click', function () { setOpen(drawer.dataset.open !== 'true'); });
      on(close, 'click', function () { setOpen(false); });
      all('a', drawer).forEach(function (a) { on(a, 'click', function () { setOpen(false); }); });
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && drawer.dataset.open === 'true') setOpen(false);
      });
    }
  }

  /* ---- 3a. wordmark auto-fit -------------------------------------------
     The hero wordmark is merchant-editable, so its length is unknown. Measure
     the rendered line and scale the font size until it fills the available
     width without overflowing. */
  function fitWordmark() {
    all('[data-mg-fit]').forEach(function (el) {
      var box = el.parentElement;
      if (!box) return;
      var pad = parseFloat(el.dataset.mgFit || '0.94');
      el.style.fontSize = '';
      var avail = box.clientWidth * pad;
      if (avail <= 0) return;
      var current = parseFloat(getComputedStyle(el).fontSize);
      var natural = el.scrollWidth;
      if (!natural) return;
      var next = current * (avail / natural);
      /* the mark may also grow to fill, but never taller than its own row */
      var ceiling = box.clientHeight / 0.86;
      el.style.fontSize = Math.max(28, Math.min(next, ceiling)) + 'px';
    });
  }

  /* ---- 3. hero curtain ------------------------------------------------- */
  function initHero() {
    var wrap = document.querySelector('[data-mg-hero]');
    if (!wrap) return;
    var curtain = wrap.querySelector('.mg-hero__curtain');
    if (!curtain) return;

    fitWordmark();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitWordmark).catch(function () {});
    }
    on(window, 'resize', fitWordmark);

    /* stagger the wordmark letters */
    all('.mg-hero__ch', wrap).forEach(function (ch, i) {
      ch.style.animationDelay = (0.08 + i * 0.055) + 's';
    });

    if (reduced()) { curtain.style.setProperty('--mg-curtain', '0'); return; }

    subscribe(function (y, vh) {
      var rect = wrap.getBoundingClientRect();
      var travel = Math.max(1, rect.height - vh);
      var p = clamp((-rect.top) / travel, 0, 1);
      curtain.style.setProperty('--mg-curtain', p.toFixed(4));
      var panel = wrap.querySelector('.mg-panel');
      if (panel) {
        panel.style.setProperty('--mg-par', p.toFixed(4));
        panel.style.setProperty('--mg-panel-scale', (0.94 + p * 0.06).toFixed(4));
      }
    });
  }

  /* ---- 3b. blossom drift ------------------------------------------------
     Falling blossom behind and in front of the wordmark. The product has real
     pressed blossoms set into it, so this is the brand's own material.

     Three things do the heavy lifting:

     1. DEPTH IS REAL, NOT FAKED. Two canvases straddle the wordmark -- a far
        layer behind it (smaller, slower, dimmer, CSS-blurred for depth of
        field) and a near layer in front (larger, faster, shadowed). The mark
        sits *inside* the weather rather than pasted on top of it.
     2. THE FIELD MOVES TOGETHER. One wind value, summed from two slow sines,
        drives every petal on both layers. Independent particles read as a
        screensaver; a field that breathes as one reads as observed.
     3. PETALS TUMBLE IN 3D. Spin and width-breathing share a phase
        relationship, so a petal narrows as it turns and widens as it comes
        back -- a flip, not a spin. Floored so it never becomes a scratch.

     Petals enter in loose streams rather than uniformly, because real drift
     clusters. One rAF loop drives all layers.                              */
  /* ---- 3c. page transitions ---------------------------------------------
     Not a single-page app: the browser still performs every navigation. All
     this does is fade the current page out first, so following a link ends
     in a dissolve rather than a white flash.

     The guards are the substance. A transition that swallows a middle-click,
     a modifier-click, a download, a new tab or a cross-origin link is worse
     than no transition, so each of those falls through untouched. And the
     navigation is driven by a timer, not by the animation: if animationend
     never arrives, the click still goes where the visitor asked.
  ------------------------------------------------------------------------ */
  function initTransitions() {
    var root = document.documentElement;
    if (!root.classList.contains('mg-anim-in') || reduced()) return;
    var leaving = false;

    on(document, 'click', function (e) {
      if (leaving || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download') || a.hasAttribute('data-mg-no-fade')) return;

      var raw = a.getAttribute('href') || '';
      if (!raw || raw.charAt(0) === '#') return;
      if (/^(mailto|tel|sms|javascript):/i.test(raw)) return;

      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      /* an in-page jump: leave it to the anchor handler */
      if (url.hash && url.pathname === window.location.pathname
          && url.search === window.location.search) return;

      e.preventDefault();
      leaving = true;
      root.classList.add('mg-leaving');

      var done = false;
      var go = function () {
        if (done) return;
        done = true;
        window.location.href = url.href;
      };
      var timer = setTimeout(go, 280);
      on(document.body, 'animationend', function (ev) {
        if (ev.animationName !== 'mgPageOut') return;
        clearTimeout(timer);
        go();
      });
    });

    /* Back/forward out of the bfcache restores the DOM as it was -- which is
       mid-fade, with the page at zero. Clear it. */
    on(window, 'pageshow', function (e) {
      if (!e.persisted) return;
      leaving = false;
      root.classList.remove('mg-leaving');
    });
  }

  /* ---- 3e. range mega-menu ----------------------------------------------
     Opens on click, not hover: a hover-only menu is unreachable by keyboard
     and fires by accident on a trackpad. Closes on Escape, on a click outside
     and when focus leaves the panel entirely, so it never strands a keyboard
     user inside it.
  ------------------------------------------------------------------------ */
  function initRangePanel() {
    var trigger = document.querySelector('.mg-nav__trigger');
    var panel = document.getElementById('mgRangePanel');
    if (!trigger || !panel) return;

    function setOpen(open) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.dataset.open = open ? 'true' : 'false';
      if (open) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    }

    on(trigger, 'click', function (e) {
      e.preventDefault();
      setOpen(trigger.getAttribute('aria-expanded') !== 'true');
    });

    /* Hover opens it too, but only where a real pointer exists. On touch,
       `hover` events fire as a synthetic pass before the tap and would open
       and immediately re-close the panel. Click still works everywhere, and
       remains the only path for keyboard users. */
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var closeTimer = 0;
      var hold = function () { clearTimeout(closeTimer); };
      /* A short delay on leaving, so crossing the gap between the trigger and
         the panel does not slam it shut mid-move. */
      var release = function () {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(function () { setOpen(false); }, 220);
      };
      var enter = function () { hold(); setOpen(true); };

      var li = trigger.closest('li') || trigger;
      on(li, 'mouseenter', enter);
      on(li, 'mouseleave', release);
      on(panel, 'mouseenter', hold);
      on(panel, 'mouseleave', release);
      /* Leaving via the keyboard or by scrolling away should still close it. */
      on(window, 'blur', function () { setOpen(false); });
    }

    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (trigger.getAttribute('aria-expanded') !== 'true') return;
      setOpen(false);
      trigger.focus();
    });

    on(document, 'click', function (e) {
      if (trigger.getAttribute('aria-expanded') !== 'true') return;
      if (panel.contains(e.target) || trigger.contains(e.target)) return;
      setOpen(false);
    });

    on(document, 'focusin', function (e) {
      if (trigger.getAttribute('aria-expanded') !== 'true') return;
      if (panel.contains(e.target) || trigger.contains(e.target)) return;
      setOpen(false);
    });

    /* A navigation that stays open behind the next page looks broken on the
       browser's back button, which restores the DOM as it was. */
    on(window, 'pageshow', function (e) { if (e.persisted) setOpen(false); });
  }

  /* ---- 3f. product gallery ----------------------------------------------
     Main plate plus thumbnail rail. The arrows step the MAIN image, not the
     rail: on a product with five shots every thumbnail already fits, so
     scrolling the rail would look like the button did nothing.
  ------------------------------------------------------------------------ */
  function initGallery() {
    all('[data-mg-gallery]').forEach(function (root) {
      var plates = all('.mg-gal__plate', root);
      var thumbs = all('[data-mg-gal-thumb]', root);
      var rail = root.querySelector('[data-mg-gal-rail]');
      var count = root.querySelector('[data-mg-gal-count]');
      var prev = root.querySelector('[data-mg-gal-prev]');
      var next = root.querySelector('[data-mg-gal-next]');
      var railPrev = root.querySelector('[data-mg-rail-prev]');
      var railNext = root.querySelector('[data-mg-rail-next]');
      if (plates.length < 2) return;
      var i = 0;
      var pad = function (n) { return n < 10 ? '0' + n : String(n); };

      function show(n) {
        i = (n + plates.length) % plates.length;
        plates.forEach(function (el, idx) { el.dataset.active = idx === i ? 'true' : 'false'; });
        thumbs.forEach(function (el, idx) { el.dataset.active = idx === i ? 'true' : 'false'; });
        if (count) count.textContent = 'Fig. ' + pad(i + 1) + ' / ' + pad(plates.length);
        if (rail && thumbs[i]) {
          var t = thumbs[i].getBoundingClientRect(), r = rail.getBoundingClientRect();
          if (t.left < r.left || t.right > r.right) {
            rail.scrollLeft += (t.left - r.left) - (r.width - t.width) / 2;
          }
        }
      }

      /* The rail arrows exist only when the rail actually overflows. Four
         thumbnails fit by design, so a product with four or fewer images
         would otherwise show two buttons that do nothing. */
      function syncRail() {
        if (!rail || !railPrev || !railNext) return;
        var over = rail.scrollWidth - rail.clientWidth > 2;
        railPrev.hidden = railNext.hidden = !over;
        if (!over) return;
        railPrev.disabled = rail.scrollLeft <= 1;
        railNext.disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;
      }

      thumbs.forEach(function (t, idx) {
        on(t, 'click', function (e) { e.preventDefault(); show(idx); });
      });
      if (prev) on(prev, 'click', function () { show(i - 1); });
      if (next) on(next, 'click', function () { show(i + 1); });
      if (railPrev) on(railPrev, 'click', function () { rail.scrollLeft -= rail.clientWidth; });
      if (railNext) on(railNext, 'click', function () { rail.scrollLeft += rail.clientWidth; });
      if (rail) {
        on(rail, 'scroll', function () { syncRail(); });
        on(window, 'resize', syncRail);
      }

      on(root, 'keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); show(i - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1); }
      });

      show(0);
      syncRail();
      /* Thumbnails load lazily, so the rail's scrollWidth is not final on the
         first pass — re-check once they have settled. */
      on(window, 'load', syncRail);
      setTimeout(syncRail, 800);
    });
  }

  /* ---- 3g. product recommendations ---------------------------------------
     Shopify only fills recommendations.products when the section is fetched
     through routes.product_recommendations_url, so the block renders empty
     inline and is refetched here. IntersectionObserver defers the request
     until it is nearly in view: it is an upsell below the buy box, and it
     should not compete with the page a visitor actually asked for.
  ------------------------------------------------------------------------ */
  function initRecs() {
    all('[data-mg-recs]').forEach(function (root) {
      var url = root.dataset.url;
      if (!url) return;
      var intent = root.dataset.intent;
      if (intent) url += '&intent=' + encodeURIComponent(intent);
      var done = false;

      function load() {
        if (done) return;
        done = true;
        fetch(url)
          .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-mg-recs] .mg-shell');
            /* No recommendations is a normal answer, not a failure — a new
               product has no data behind it yet. Leave the section empty. */
            if (fresh) root.appendChild(fresh);
          })
          .catch(function () { /* upsell is not worth an error state */ });
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) { io.disconnect(); load(); } });
        }, { rootMargin: '600px 0px' });
        io.observe(root);
        /* Belt and braces. The element is 1px tall until it has content, and
           a deferral that silently never resolves is worse than an early
           request for something this small. */
        setTimeout(function () { io.disconnect(); load(); }, 2500);
      } else {
        load();
      }
    });
  }

  /* ---- 3h. announcement bar ---------------------------------------------
     Clicking the code copies it. The bar is still a link to the shop, so the
     copy handler stops propagation rather than preventing default globally —
     a click anywhere else on the bar should still navigate.
  ------------------------------------------------------------------------ */
  function initAnnounce() {
    var bar = document.querySelector('[data-mg-announce]');
    if (!bar) return;
    var code = bar.querySelector('[data-mg-code]');
    if (!code) return;
    var live = bar.querySelector('[data-mg-announce-live]');
    var label = code.dataset.copied || 'Copied';
    var timer = 0;

    on(code, 'click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var value = code.dataset.mgCode || code.textContent.trim();
      var done = function () {
        code.dataset.copied = 'true';
        if (live) live.textContent = label + ': ' + value;
        clearTimeout(timer);
        timer = setTimeout(function () {
          delete code.dataset.copied;
          if (live) live.textContent = '';
        }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(function () { /* nothing to do */ });
      } else {
        /* Older Safari and any non-secure context: select it so the visitor
           can copy manually rather than silently doing nothing. */
        var r = document.createRange();
        r.selectNodeContents(code);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
        done();
      }
    });
  }

  /* ---- search ----------------------------------------------------------
     Typeahead against Shopify's own predictive endpoint. The form underneath
     is a plain GET to routes.search_url, so with JS off -- or if the fetch
     fails -- typing and pressing Enter still lands on the real results page.
     The suggestions are an enhancement, never the only way through. */
  function initSearch() {
    var root = document.getElementById('mgSearch');
    if (!root) return;
    var panel    = root.querySelector('.mg-search__panel');
    var form     = root.querySelector('.mg-search__form');
    var input    = root.querySelector('.mg-search__input');
    var results  = root.querySelector('[data-mg-search-results]');
    var quick    = root.querySelector('[data-mg-search-quick]');
    var clearBtn = root.querySelector('[data-mg-search-clear]');
    if (!input || !results) return;

    var searchUrl = (form && form.getAttribute('action')) || '/search';
    /* routes.predictive_search_url is /search/suggest, which serves rendered
       sections. The .json variant is the one that returns data. */
    var api = (root.dataset.endpoint || '/search/suggest').replace(/\/+$/, '');
    if (!/\.json$/.test(api)) api += '.json';

    var opener = null, timer = 0, ctrl = null, rows = [], sel = -1;

    var money = null;
    try {
      money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: root.dataset.currency || 'USD'
      });
    } catch (e) { money = null; }

    function esc(v) {
      return String(v == null ? '' : v)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    function price(v) {
      var n = parseFloat(v);
      if (isNaN(n)) return '';
      return money ? money.format(n) : n.toFixed(2);
    }
    function thumb(p) {
      var u = (p.featured_image && p.featured_image.url) || p.image;
      if (!u) return '';
      return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=140';
    }
    function abort() { if (ctrl) { try { ctrl.abort(); } catch (e) {} ctrl = null; } }

    function resetList() {
      abort();
      results.innerHTML = '';
      rows = []; sel = -1;
      input.setAttribute('aria-expanded', 'false');
      if (quick) quick.hidden = false;
    }

    function setOpen(open, from) {
      if (open) {
        opener = from || null;
        root.removeAttribute('hidden');
        void root.offsetWidth;          /* reflow: give the transition a start state */
        root.dataset.open = 'true';
        document.documentElement.style.overflow = 'hidden';
        requestAnimationFrame(function () { input.focus(); });
      } else {
        root.dataset.open = 'false';
        document.documentElement.style.overflow = '';
        abort();
        clearTimeout(timer);
        var hide = function () {
          if (root.dataset.open !== 'true') root.setAttribute('hidden', '');
        };
        if (reduced()) hide(); else setTimeout(hide, 340);
        /* the drawer button that opened this is gone once the drawer closed,
           so fall back to the one in the nav bar rather than losing focus */
        if (opener && opener.offsetParent === null) opener = document.querySelector('.mg-nav__search');
        if (opener) { try { opener.focus(); } catch (e) {} }
        opener = null;
      }
      all('[data-mg-search-open]').forEach(function (b) {
        if (b.hasAttribute('aria-expanded')) {
          b.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
      });
    }

    function render(q, data) {
      var r  = (data && data.resources && data.resources.results) || {};
      var ps = r.products || [];
      var cs = r.collections || [];
      var html = '';

      if (cs.length) {
        html += '<div class="mg-search__group">Collections</div>';
        cs.forEach(function (c) {
          html += '<a class="mg-search__row" role="option" href="' + esc(c.url) + '">' +
                  '<span class="mg-search__meta"><span class="mg-search__title">' +
                  esc(c.title) + '</span></span></a>';
        });
      }
      if (ps.length) {
        html += '<div class="mg-search__group">Products</div>';
        ps.forEach(function (p) {
          var t = thumb(p);
          html += '<a class="mg-search__row" role="option" href="' + esc(p.url) + '">' +
            (t ? '<img class="mg-search__thumb" src="' + esc(t) + '" alt="" loading="lazy" width="52" height="52">'
               : '<span class="mg-search__thumb"></span>') +
            '<span class="mg-search__meta">' +
              '<span class="mg-search__title">' + esc(p.title) + '</span>' +
              '<span class="mg-search__price">' +
                (p.available ? esc(price(p.price)) : '<em>Sold out</em>') +
              '</span>' +
            '</span></a>';
        });
      }

      if (!html) {
        results.innerHTML = '<p class="mg-search__note">No matches for &ldquo;' + esc(q) +
          '&rdquo;. Try a size (140mm) or a shape (round, oval).</p>';
        rows = []; sel = -1;
        input.setAttribute('aria-expanded', 'false');
      } else {
        html += '<a class="mg-search__all" href="' + esc(searchUrl) + '?q=' +
                encodeURIComponent(q) + '">See all results <span aria-hidden="true">&#8594;</span></a>';
        results.innerHTML = html;
        rows = all('.mg-search__row', results);
        sel = -1;
        input.setAttribute('aria-expanded', 'true');
      }
      if (quick) quick.hidden = true;
    }

    function run(q) {
      abort();
      var url = api + '?q=' + encodeURIComponent(q) +
        '&resources%5Btype%5D=product,collection' +
        '&resources%5Blimit%5D=6' +
        '&resources%5Boptions%5D%5Bunavailable_products%5D=last';
      if ('AbortController' in window) ctrl = new AbortController();
      fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (d) { if (d) render(q, d); })
        .catch(function () { /* the form still submits; failing quietly is correct */ });
    }

    on(input, 'input', function () {
      var q = input.value.trim();
      if (clearBtn) clearBtn.hidden = !q;
      clearTimeout(timer);
      if (q.length < 2) { resetList(); return; }
      timer = setTimeout(function () { run(q); }, 180);
    });

    on(input, 'keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!rows.length) return;
        e.preventDefault();
        sel = e.key === 'ArrowDown'
          ? (sel + 1) % rows.length
          : (sel <= 0 ? rows.length - 1 : sel - 1);
        rows.forEach(function (row, i) {
          row.setAttribute('aria-selected', i === sel ? 'true' : 'false');
        });
        rows[sel].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && sel > -1 && rows[sel]) {
        e.preventDefault();
        window.location.href = rows[sel].getAttribute('href');
      }
    });

    on(form, 'submit', function (e) { if (!input.value.trim()) e.preventDefault(); });

    on(clearBtn, 'click', function () {
      input.value = '';
      clearBtn.hidden = true;
      resetList();
      input.focus();
    });

    if (quick) {
      all('.mg-search__chip', quick).forEach(function (chip) {
        on(chip, 'click', function () {
          input.value = chip.dataset.term || chip.textContent.trim();
          if (clearBtn) clearBtn.hidden = false;
          input.focus();
          run(input.value.trim());
        });
      });
    }

    all('[data-mg-search-open]').forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        /* opened from inside the drawer: close that first, or we stack two
           overlays and two scroll locks and the second close leaves the page
           unable to scroll */
        var drawer = document.querySelector('.mg-drawer-nav[data-open="true"]');
        if (drawer) {
          var dc = drawer.querySelector('.mg-drawer-nav__close');
          if (dc) dc.click();
        }
        setOpen(true, btn);
      });
    });
    all('[data-mg-search-close]', root).forEach(function (b) {
      on(b, 'click', function () { setOpen(false); });
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && root.dataset.open === 'true') setOpen(false);
    });

    /* keep Tab inside the dialog while it is open */
    on(panel, 'keydown', function (e) {
      if (e.key !== 'Tab') return;
      var f = all('a[href], button:not([disabled]), input', panel).filter(function (el) {
        return el.offsetParent !== null && !el.hasAttribute('hidden');
      });
      if (!f.length) return;
      var first = f[0], lastEl = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); first.focus(); }
    });
  }

  function initPetals() {
    var layers = all('[data-mg-petals]');
    if (!layers.length || reduced()) return;

    var clock = 0, raf = 0, running = false, last = 0, groundClock = 9;
    var fields = [];

    /* one wind for the whole scene, so both layers gust together */
    function wind(t) {
      return Math.sin(t * 0.14) * 26 + Math.sin(t * 0.37 + 1.7) * 11;
    }

    layers.forEach(function (canvas) {
      var ctx = canvas.getContext && canvas.getContext('2d');
      if (!ctx) return;
      var count = Math.max(0, parseInt(canvas.dataset.mgPetals || '0', 10));
      if (!count) return;

      var f = {
        canvas: canvas, ctx: ctx, count: count,
        alpha: parseFloat(canvas.dataset.mgPetalOpacity || '0.8'),
        baseScale: parseFloat(canvas.dataset.mgPetalScale || '1'),
        sized: parseFloat(canvas.dataset.mgPetalScale || '1'),
        speed: parseFloat(canvas.dataset.mgPetalSpeed || '1'),
        shadow: canvas.dataset.mgPetalShadow === 'true',
        tones: (canvas.dataset.mgPetalTones || '#e7bcc6').split(','),
        petals: [], w: 0, h: 0, grads: {},
        streams: [0.12, 0.3, 0.5, 0.68, 0.88]   /* loose lanes, not uniform */
      };

      f.resize = function () {
        var r = canvas.getBoundingClientRect();
        if (!r.width || !r.height) return false;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var prevW = f.w, prevH = f.h;
        var pxW = Math.round(r.width * dpr), pxH = Math.round(r.height * dpr);

        f.w = r.width; f.h = r.height;
        /* Assigning width/height clears the canvas, so only do it when the
           backing store genuinely changed size. */
        if (canvas.width !== pxW || canvas.height !== pxH) {
          canvas.width = pxW; canvas.height = pxH;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        /* Carry the existing petals into the new box rather than dropping
           them. Re-seeding here is what made the field jump on every mobile
           scroll, because a collapsing URL bar fires resize continuously. */
        if (prevW && prevH && f.petals.length) {
          var sx = f.w / prevW, sy = f.h / prevH;
          if (sx !== 1 || sy !== 1) {
            for (var i = 0; i < f.petals.length; i++) {
              f.petals[i].x *= sx;
              f.petals[i].y *= sy;
            }
          }
        }
        return { prevW: prevW, prevH: prevH };
      };

      f.seed = function (p, above) {
        var d = Math.random();
        var lane = f.streams[(Math.random() * f.streams.length) | 0];
        p.d = d;
        p.size = (9 + d * 15) * f.sized;
        p.x = f.w * lane + (Math.random() - 0.5) * f.w * 0.26;
        p.y = above ? -30 - Math.random() * f.h * 0.6 : Math.random() * f.h;
        p.fall = (11 + d * 20) * f.speed;
        p.drag = 0.35 + d * 0.65;               /* near petals catch more wind */
        p.sway = 10 + Math.random() * 26;
        p.swayPhase = Math.random() * Math.PI * 2;
        p.swaySpeed = 0.22 + Math.random() * 0.3;
        p.spin = Math.random() * Math.PI * 2;
        p.phase = Math.random() * Math.PI * 2;
        /* Roughly one in four is a whole flower. Any more and it stops being
           weather and starts being a pattern. A bloom carries more air, so it
           falls slower, swings wider and turns lazier than a loose petal. */
        p.bloom = Math.random() < 0.26;
        if (p.bloom) {
          /* the path runs 1.46 units from stem to tip, so these factors are
             chosen to give a bloom about half again the span of a loose petal */
          p.s = p.size * 0.92;
          p.fall *= 0.78;
          p.sway *= 1.25;
          p.spinSpeed = (Math.random() - 0.5) * 0.42;
        } else {
          p.s = p.size * 1.22;
          p.spinSpeed = (Math.random() - 0.5) * 0.8;
        }
        p.tone = f.tones[(Math.random() * f.tones.length) | 0].trim();
        p.alpha = (0.5 + d * 0.5) * f.alpha * (p.bloom ? 1.12 : 1);
        return p;
      };

      /* Density and size follow viewport area, not the raw setting. Sixteen
         blossoms read as a light drift across a 1440x900 desktop and as a
         snowstorm on a 390x844 phone, which is a quarter of the area. Scaling
         by the square root of the area ratio splits the difference between
         constant count (too busy small) and constant petals-per-pixel (too
         sparse small). */
      f.gauge = function () {
        var k = Math.sqrt((f.w * f.h) / (1440 * 900));
        return Math.min(Math.max(k, 0.42), 1.3);
      };
      f.build = function () {
        f.petals.length = 0;
        var k = f.gauge();
        f.sized = f.baseScale * Math.min(Math.max(k, 0.78), 1.12);
        var n = Math.max(0, Math.round(f.count * k));
        for (var i = 0; i < n; i++) f.petals.push(f.seed({}, false));
      };

      if (!f.resize()) return;
      f.build();
      fields.push(f);
    });

    if (!fields.length) return;

    function rgbOf(hex) {
      var h = String(hex).replace('#', '').trim();
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      var n = parseInt(h, 16);
      if (isNaN(n)) return [231, 188, 198];
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    /* nudge an [r,g,b] toward white (+) or toward ink (-) */
    function toward(rgb, amt) {
      var mix = function (c) {
        return Math.max(0, Math.min(255, Math.round(amt > 0
          ? c + (255 - c) * amt
          : c * (1 + amt))));
      };
      return 'rgb(' + mix(rgb[0]) + ',' + mix(rgb[1]) + ',' + mix(rgb[2]) + ')';
    }
    /* Lifting a tone toward white is the obvious way to brighten it and the
       wrong one here: it strips the chroma, and over ink at partial alpha the
       petal then composites to grey. Push the channels away from their own
       mean first -- saturate -- and lighten only a little. What survives the
       compositing is still recognisably pink. */
    function vivid(rgb, lightAmt, satAmt) {
      var mean = (rgb[0] + rgb[1] + rgb[2]) / 3;
      var out = [], i, c;
      for (i = 0; i < 3; i++) {
        c = mean + (rgb[i] - mean) * (1 + satAmt);
        c = c + (255 - c) * lightAmt;
        out.push(Math.max(0, Math.min(255, Math.round(c))));
      }
      return out;
    }

    /* ---- what is the petal falling over? ---------------------------------
       A translucent pink petal composited over near-black lands on a muddy
       grey-brown. That is correct for a filter and wrong for an object: a
       real petal catches light and reads *brighter* than the ground behind
       it. So the drift asks the page what it is currently over.

       The measurement is per section per tick, not per petal per frame --
       every one of our sections carries .mg-sec, so their own resolved
       background colour is the source of truth and no section needs to
       declare anything. Sections lighter than the luminance cut are simply
       not recorded, which makes the common case an empty list. */
    var grounds = [];
    var pageDark = false;
    /* Where the page was when the bands above were measured, and how far it
       has moved since. The bands are viewport-relative, so on a fast scroll a
       120ms-old measurement is hundreds of pixels out and petals flicker
       between their light and dark treatment at every section edge. Reading
       one scroll offset per frame is far cheaper than re-measuring, and keeps
       the bands exact between measurements. */
    var groundsAt = 0;
    var groundShift = 0;

    function lumOf(color) {
      var m = /rgba?\(([^)]+)\)/.exec(color || '');
      if (!m) return null;
      var parts = m[1].split(',');
      if (parts.length > 3 && parseFloat(parts[3]) < 0.5) return null;
      var l = 0.2126 * parseFloat(parts[0])
            + 0.7152 * parseFloat(parts[1])
            + 0.0722 * parseFloat(parts[2]);
      return isNaN(l) ? null : l;
    }

    function measureGrounds() {
      grounds.length = 0;
      /* The page's own ground first. A dark template (the 404) paints the
         body, and the band above the first section belongs to that, not to
         any section -- keying only off sections left those petals grey. */
      var pl = lumOf(window.getComputedStyle(document.body).backgroundColor);
      pageDark = pl !== null && pl <= 90;

      /* .mg-sec covers the page's own sections; [data-mg-ground] lets any
         other element with its own background join in — the announcement bar
         is ink on a bone page, and without this the engine judged its petals
         against the page ground and drew them dark on dark. */
      groundsAt = window.pageYOffset || 0;
      var secs = document.querySelectorAll('.mg-sec, [data-mg-ground]');
      for (var i = 0; i < secs.length; i++) {
        var l = lumOf(window.getComputedStyle(secs[i]).backgroundColor);
        if (l === null) continue;
        /* only record sections that DISAGREE with the page ground, so the
           list stays short and the test is one XOR */
        if ((l <= 90) === pageDark) continue;
        var r = secs[i].getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) continue;
        grounds.push(r.top, r.bottom);
      }
    }

    function overDark(y) {
      var v = y + groundShift;          /* correct the bands for scroll since measuring */
      var flipped = false;
      for (var i = 0; i < grounds.length; i += 2) {
        if (v >= grounds[i] && v <= grounds[i + 1]) { flipped = true; break; }
      }
      return pageDark !== flipped;
    }

    function petalFill(f, tone, size, lit) {
      var key = tone + '|' + Math.round(size / 3) + (lit ? '|L' : '');
      var hit = f.grads[key];
      if (hit) return hit;
      var base = rgbOf(tone);
      if (lit) base = vivid(base, 0.12, 0.55);
      var g = f.ctx.createLinearGradient(0, 0, 0, -size * 1.46);
      g.addColorStop(0, toward(base, -0.16));
      g.addColorStop(0.55, toward(base, 0));
      g.addColorStop(1, toward(base, 0.30));
      f.grads[key] = g;
      return g;
    }

    /* Petal geometry is anchored at the stem, not the centre. A single falling
       petal is drawn offset so it turns about its own middle; a bloom reuses
       the same path five times around the stem. One shape, two uses. */
    function petalPath(ctx, s) {
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(s * 0.58, -s * 0.06, s * 0.80, -s * 0.88, s * 0.24, -s * 1.46);
      ctx.quadraticCurveTo(0, -s * 1.18, -s * 0.24, -s * 1.46);   /* the notch */
      ctx.bezierCurveTo(-s * 0.80, -s * 0.88, -s * 0.58, -s * 0.06, 0, 0);
      ctx.closePath();
    }

    /* The heart of the bloom: pigment pools where the five petals meet, so a
       short radial wash sits under the stamens and fades out before the
       petal edges. Cached alongside the petal gradients. */
    function heartFill(f, tone, s, lit) {
      var key = 'h' + tone + '|' + Math.round(s / 2) + (lit ? '|L' : '');
      var hit = f.grads[key];
      if (hit) return hit;
      var base = rgbOf(tone);
      if (lit) base = vivid(base, 0.12, 0.55);
      var g = f.ctx.createRadialGradient(0, 0, 0, 0, 0, Math.max(1, s * 0.52));
      g.addColorStop(0, toward(base, -0.30));
      g.addColorStop(0.55, toward(base, -0.10));
      g.addColorStop(1, 'rgba(255,255,255,0)');
      f.grads[key] = g;
      return g;
    }

    function drawPetal(f, p, s, lit) {
      var ctx = f.ctx;
      ctx.save();
      ctx.translate(0, s * 0.73);            /* turn about the petal's middle */
      ctx.fillStyle = petalFill(f, p.tone, s, lit);
      petalPath(ctx, s);
      ctx.fill();
      ctx.restore();
    }

    /* A whole sakura: five petals around the stem, each nudged off the exact
       72 degrees so no two blooms are the same flower, then the heart and a
       short stamen cluster over the join. Stamens are dropped on the small
       far-layer blooms, where they would only muddy the silhouette. */
    function drawBloom(f, p, s, lit) {
      var ctx = f.ctx, i, a;
      for (i = 0; i < 5; i++) {
        a = i * 1.2566 + Math.sin(i * 2.3 + p.phase) * 0.11;
        ctx.save();
        ctx.rotate(a);
        ctx.fillStyle = petalFill(f, p.tone, s, lit);
        petalPath(ctx, s);
        ctx.fill();
        ctx.restore();
      }
      ctx.fillStyle = heartFill(f, p.tone, s, lit);
      ctx.beginPath();
      ctx.arc(0, 0, s * 0.52, 0, 6.2832);
      ctx.fill();
      if (s > 6.5) {
        ctx.fillStyle = lit ? 'rgba(236,206,148,0.85)' : 'rgba(190,142,70,0.8)';
        for (i = 0; i < 6; i++) {
          a = i * 1.0472 + p.phase;
          ctx.beginPath();
          ctx.arc(Math.cos(a) * s * 0.30, Math.sin(a) * s * 0.30, s * 0.08, 0, 6.2832);
          ctx.fill();
        }
      }
    }

    function frame(now) {
      raf = 0;
      if (!running) return;
      var dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016;
      last = now;
      clock += dt;
      groundClock += dt;
      if (groundClock > 0.12) { groundClock = 0; measureGrounds(); }
      groundShift = (window.pageYOffset || 0) - groundsAt;
      var gust = wind(clock);

      for (var k = 0; k < fields.length; k++) {
        var f = fields[k], ctx = f.ctx;
        ctx.clearRect(0, 0, f.w, f.h);
        for (var i = 0; i < f.petals.length; i++) {
          var p = f.petals[i];
          p.y += p.fall * dt;
          p.x += gust * p.drag * dt;
          p.swayPhase += p.swaySpeed * dt;
          p.spin += p.spinSpeed * dt;

          if (p.y - p.size > f.h) f.seed(p, true);
          if (p.x < -60) p.x = f.w + 40;
          else if (p.x > f.w + 60) p.x = -40;

          var x = p.x + Math.sin(p.swayPhase) * p.sway;
          /* width follows the spin, so the petal flips rather than pinwheels */
          var squash = Math.abs(Math.cos(p.spin * 1.3)) * 0.62 + 0.38;

          ctx.save();
          ctx.translate(x, p.y);
          ctx.rotate(p.spin);
          ctx.scale(squash, 1);
          ctx.globalAlpha = p.alpha * (0.62 + squash * 0.38);
          if (f.shadow && p.d > 0.5) {
            ctx.shadowColor = 'rgba(23, 24, 26, 0.16)';
            ctx.shadowBlur = 6 + p.d * 8;
            ctx.shadowOffsetY = 3;
          }
          /* Over ink the petal is a lit object, not a wash: the shadow is a
             light-ground device that only muddies it, and the alpha that reads
             as a whisper on bone composites to dark grey on near-black. Both
             the tone and the alpha have to move, or the hue never survives. */
          var lit = overDark(p.y);
          if (lit) {
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            /* Lit, but never opaque. The ceiling matters as much as the
               floor: at full alpha a bloom stops being weather and starts
               covering the headline underneath it. */
            ctx.globalAlpha = Math.min(0.66, Math.max(ctx.globalAlpha * 1.9, 0.34));
          }
          if (p.bloom) drawBloom(f, p, p.s, lit);
          else drawPetal(f, p, p.s, lit);
          ctx.restore();
        }
      }
      raf = requestAnimationFrame(frame);
    }

    function start() {
      if (running || reduced()) return;
      running = true; last = 0;
      if (!raf) raf = requestAnimationFrame(frame);
    }
    function stop() {
      running = false;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
    }

    /* Coalesced into one frame: a mobile resize arrives as a burst. */
    var resizeRaf = 0;
    on(window, 'resize', function () {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(function () {
        resizeRaf = 0;
        fields.forEach(function (f) {
          var prev = f.resize();
          if (!prev) return;
          if (!f.petals.length) { f.build(); return; }
          /* Rebuild ONLY on a width change — that means a real layout change
             (rotation, a desktop window drag), where the petal count and size
             should be re-gauged. Height-only changes are the browser's own
             chrome sliding in and out and must never scatter the field. */
          if (Math.abs(f.w - prev.prevW) > 1) f.build();
        });
      });
    });
    on(document, 'visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    /* Observe every field, not just the first. The hero's layer scrolls away
       but the site-wide one is fixed and always on screen, so keying the loop
       to fields[0] would have frozen the whole drift the moment the hero
       left the viewport. */
    if ('IntersectionObserver' in window) {
      var live = 0;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { if (!e.target.dataset.mgLive) { e.target.dataset.mgLive = '1'; live++; } }
          else if (e.target.dataset.mgLive) { delete e.target.dataset.mgLive; live--; }
        });
        if (live > 0) start(); else stop();
      }, { threshold: 0 });
      fields.forEach(function (f) { io.observe(f.canvas); });
    } else {
      start();
    }
  }

  /* ---- 4. generic pinned stepper --------------------------------------
     Used by the range rail and the story scrub. Divides the scroll length of
     [data-mg-track] into N steps and marks the matching [data-step] active. */
  function stepper(root, opts) {
    var track = root.querySelector('[data-mg-track]');
    var stage = root.querySelector('[data-mg-stage]');
    if (!track || !stage) return null;

    var groups = opts.groups;               /* array of NodeList-ish arrays  */
    var count = opts.count;
    var current = -1;
    var progressEl = opts.progress || null;

    function apply(i, p) {
      if (i !== current) {
        current = i;
        groups.forEach(function (g) {
          g.forEach(function (el, idx) {
            el.dataset.active = idx === i ? 'true' : 'false';
          });
        });
        (opts.tabs || []).forEach(function (btn, idx) {
          btn.setAttribute('aria-selected', idx === i ? 'true' : 'false');
        });
        if (opts.onChange) opts.onChange(i);
      }
      if (progressEl) progressEl.style.setProperty('--mg-progress', p.toFixed(4));
    }

    function measure() {
      if (!DESKTOP.matches || reduced()) {
        track.style.height = '';
        return;
      }
      /* Scroll cost per step. 100svh means a full screen of scrolling per
         slide, which adds up fast on a five-slide rail -- the section alone
         can own a third of the page before a visitor reaches anything
         buyable. Tunable per section from the editor. */
      var step = parseFloat(root.dataset.mgStep || '100');
      if (!(step > 0)) step = 100;
      track.style.height = (count * step) + 'svh';
    }

    subscribe(function (y, vh) {
      if (!DESKTOP.matches || reduced()) { apply(current < 0 ? 0 : current, 0); return; }
      var rect = track.getBoundingClientRect();
      var travel = Math.max(1, rect.height - vh);
      var p = clamp((-rect.top) / travel, 0, 1);
      var i = clamp(Math.floor(p * count - 1e-6), 0, count - 1);
      apply(i, p);
    });

    on(window, 'resize', measure);
    measure();
    apply(0, 0);

    return {
      go: function (i) {
        if (!DESKTOP.matches || reduced()) { apply(i, 0); return; }
        var rect = track.getBoundingClientRect();
        var top = rect.top + window.pageYOffset;
        var travel = Math.max(1, rect.height - window.innerHeight);
        var target = top + travel * ((i + 0.5) / count);
        window.scrollTo({ top: target, behavior: 'smooth' });
      },
      apply: apply
    };
  }

  /* ---- 5. range rail --------------------------------------------------
     Desktop: a pinned stepper cross-fades one slide at a time.
     Mobile:  the same slides become a horizontal scroll-snap track, so the
              dots scroll rather than switch, and the counter follows the
              nearest snapped slide. */
  function initRange() {
    all('[data-mg-range]').forEach(function (root) {
      var slides = all('.mg-slide', root);
      var ghosts = all('.mg-range__ghost', root);
      var tabs = all('.mg-dots button', root);
      var counter = root.querySelector('[data-mg-counter]');
      var body = root.querySelector('.mg-range__body');
      if (!slides.length) return;

      function label(i) {
        if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
        tabs.forEach(function (t, idx) {
          t.setAttribute('aria-selected', idx === i ? 'true' : 'false');
        });
      }

      var api = stepper(root, {
        count: slides.length,
        groups: [slides, ghosts],
        tabs: tabs,
        onChange: function (i) {
          if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
        }
      });

      tabs.forEach(function (b2, i) {
        on(b2, 'click', function () {
          if (DESKTOP.matches && !reduced() && api) { api.go(i); return; }
          if (slides[i] && slides[i].scrollIntoView) {
            slides[i].scrollIntoView({
              behavior: reduced() ? 'auto' : 'smooth',
              inline: 'center',
              block: 'nearest'
            });
          }
        });
      });

      /* keep the counter honest while the visitor swipes */
      if (body) {
        var raf = 0;
        on(body, 'scroll', function () {
          if (DESKTOP.matches) return;
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = 0;
            var mid = body.scrollLeft + body.clientWidth / 2;
            var best = 0, bestD = Infinity;
            slides.forEach(function (sl, i) {
              var c = sl.offsetLeft + sl.offsetWidth / 2;
              var d = Math.abs(c - mid);
              if (d < bestD) { bestD = d; best = i; }
            });
            label(best);
          });
        }, { passive: true });
      }
    });
  }

  /* ---- 6. story scrub --------------------------------------------------
     Desktop: scroll drives which chapter panel is showing.
     Mobile:  the panels are a horizontal snap track, so the year buttons
              scroll to a chapter and the rail follows the swipe. */
  function initStory() {
    all('[data-mg-story]').forEach(function (root) {
      var panels = all('.mg-story__panel', root);
      var tabs = all('.mg-story__rail button', root);
      var deck = root.querySelector('.mg-story__panels');
      if (!panels.length) return;

      var api = stepper(root, {
        count: panels.length,
        groups: [panels],
        tabs: tabs,
        progress: root.querySelector('.mg-story__bar')
      });

      tabs.forEach(function (b2, i) {
        on(b2, 'click', function () {
          if (DESKTOP.matches && !reduced() && api) { api.go(i); return; }
          if (panels[i] && panels[i].scrollIntoView) {
            panels[i].scrollIntoView({
              behavior: reduced() ? 'auto' : 'smooth',
              inline: 'center',
              block: 'nearest'
            });
          }
        });
      });

      if (deck) {
        var raf = 0;
        on(deck, 'scroll', function () {
          if (DESKTOP.matches) return;
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = 0;
            var mid = deck.scrollLeft + deck.clientWidth / 2;
            var best = 0, bestD = Infinity;
            panels.forEach(function (pn, i) {
              var c = pn.offsetLeft + pn.offsetWidth / 2;
              var d = Math.abs(c - mid);
              if (d < bestD) { bestD = d; best = i; }
            });
            tabs.forEach(function (t, idx) {
              t.setAttribute('aria-selected', idx === best ? 'true' : 'false');
            });
          });
        }, { passive: true });
      }
    });
  }

  /* ---- 7. anatomy tabs (click, not scroll) ----------------------------- */
  function initAnatomy() {
    all('[data-mg-anatomy]').forEach(function (root) {
      var panels = all('.mg-anat', root);
      var tabs = all('.mg-tabs button', root);
      if (!panels.length) return;

      function select(i) {
        panels.forEach(function (p, idx) {
          p.dataset.active = idx === i ? 'true' : 'false';
          if (idx === i) {
            var m = p.querySelector('.mg-anat__meter i');
            if (m) {
              var target = m.dataset.fill || '1';
              if (reduced() || document.hidden) {
                m.style.setProperty('--mg-fill', target);
              } else {
                m.style.setProperty('--mg-fill', '0');
                requestAnimationFrame(function () {
                  requestAnimationFrame(function () {
                    m.style.setProperty('--mg-fill', target);
                  });
                });
              }
            }
          }
        });
        tabs.forEach(function (t, idx) {
          t.setAttribute('aria-selected', idx === i ? 'true' : 'false');
        });
      }

      /* Desktop: scroll drives the stage, using the shared pinned stepper.
         The tabs stop being the mechanism and become the indicator — the
         stepper sets aria-selected — but they still work as a keyboard route,
         scrolling to their stage instead of switching in place. */
      var pinned = null;
      if (DESKTOP.matches && !reduced()) {
        pinned = stepper(root, {
          groups: [panels],
          count: panels.length,
          tabs: tabs,
          onChange: select,
        });
      }
      if (pinned) {
        tabs.forEach(function (t, i) {
          on(t, 'click', function (e) { e.preventDefault(); pinned.go(i); });
        });
      }

      var deck = root.querySelector('.mg-anatomy__grid');

      /* On mobile the panels are a swipe track, so a tab scrolls to its stage
         rather than switching one in place. */
      if (deck) {
        var raf = 0;
        on(deck, 'scroll', function () {
          if (DESKTOP.matches) return;
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = 0;
            var mid = deck.scrollLeft + deck.clientWidth / 2;
            var best = 0, bestD = Infinity;
            panels.forEach(function (pn, i) {
              var c = pn.offsetLeft + pn.offsetWidth / 2;
              var d = Math.abs(c - mid);
              if (d < bestD) { bestD = d; best = i; }
            });
            tabs.forEach(function (t, idx) {
              t.setAttribute('aria-selected', idx === best ? 'true' : 'false');
            });
          });
        }, { passive: true });
      }

      tabs.forEach(function (t, i) {
        on(t, 'click', function () {
          if (!DESKTOP.matches && panels[i] && panels[i].scrollIntoView) {
            panels[i].scrollIntoView({
              behavior: reduced() ? 'auto' : 'smooth',
              inline: 'start',
              block: 'nearest'
            });
            tabs.forEach(function (t2, idx) {
              t2.setAttribute('aria-selected', idx === i ? 'true' : 'false');
            });
            return;
          }
          select(i);
        });
        on(t, 'keydown', function (e) {
          var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          var next = (i + d + tabs.length) % tabs.length;
          tabs[next].focus();
          select(next);
        });
      });

      select(0);

      /* re-run the meter when the block first scrolls into view */
      if ('IntersectionObserver' in window && !reduced()) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { select(0); io.disconnect(); }
          });
        }, { threshold: 0.25 });
        io.observe(root);
      }
    });
  }

  /* ---- 8. marquee ------------------------------------------------------ */
  function initMarquee() {
    all('.mg-marquee').forEach(function (mq) {
      var row = mq.querySelector('.mg-marquee__row');
      if (!row || row.dataset.cloned === 'true') return;
      var run = row.firstElementChild;
      if (!run) return;
      /* duplicate the run once so the -50% keyframe loops seamlessly */
      row.appendChild(run.cloneNode(true));
      row.dataset.cloned = 'true';
      var speed = parseFloat(mq.dataset.speed || '60');      /* px per second */
      var dur = Math.max(12, run.getBoundingClientRect().width / speed);
      mq.style.setProperty('--mg-mq-dur', dur.toFixed(1) + 's');
    });
  }

  /* ---- 9. cart --------------------------------------------------------
     Nothing to do here: the header renders Dawn's own `cart-icon-bubble`
     markup, and Dawn re-renders that section after every cart change. As a
     result this layer makes no network requests of any kind. */

  /* ---- 10. smooth in-page anchors -------------------------------------- */
  function initAnchors() {
    all('a[href^="#"]').forEach(function (a) {
      var id = a.getAttribute('href');
      if (!id || id === '#' || id.length < 2) return;
      on(a, 'click', function (e) {
        var t = document.getElementById(id.slice(1));
        if (!t) return;
        e.preventDefault();
        var top = t.getBoundingClientRect().top + window.pageYOffset -
                  (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--mg-nav-h'), 10) || 72);
        window.scrollTo({ top: top, behavior: reduced() ? 'auto' : 'smooth' });
        t.setAttribute('tabindex', '-1');
        t.focus({ preventScroll: true });
      });
    });
  }

  /* ---- boot ------------------------------------------------------------ */
  function boot() {
    initReveal();
    initNav();
    initHero();
    initPetals();
    initTransitions();
    initRangePanel();
    initSearch();
    initGallery();
    initRecs();
    initAnnounce();
    initRange();
    initStory();
    initAnatomy();
    initMarquee();
    initAnchors();
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Theme editor: re-init when a section is re-rendered. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', schedule);
})();
